import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { buildApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemo } from '../../../contexts/DemoContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { getChartTruncationConfig } from '../../../utils/chartUtils';

const IncomeProjectionChart = ({ incomeSources, projectionHorizon = 20 }) => {
  const [viewMode, setViewMode] = useState('combined');
  const [inflationRate, setInflationRate] = useState(2.5);
  const [showInflationAdjusted, setShowInflationAdjusted] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams, isDemoModeActive } = useDemo();
  const { canProjectBeyond5Years } = useSubscription();

  const viewModeOptions = [
    { value: 'combined', label: 'Combined View' },
    { value: 'by-type', label: 'By Income Type' },
    { value: 'individual', label: 'Individual Sources' }
  ];

  const calculateAnnualAmount = (amount, frequency) => {
    const multipliers = {
      monthly: 12,
      quarterly: 4,
      annually: 1,
      'one-time': 1
    };
    return amount * (multipliers[frequency] || 1);
  };

  const generateProjectionData = () => {
    const currentYear = new Date().getFullYear();
    const safeHorizon = Math.max(1, Math.min(60, Number(projectionHorizon) || 20));
    const years = Array.from({ length: safeHorizon + 1 }, (_, i) => currentYear + i);
    
    return years.map(year => {
      const yearData = { year };
      
      if (viewMode === 'combined') {
        let totalIncome = 0;
        let totalInflationAdjusted = 0;
        
        incomeSources.forEach(source => {
          if (year >= source.startYear && year <= source.endYear) {
            const yearsFromStart = year - source.startYear;
            const projectedAmount = source.currentAmount * Math.pow(1 + (source.growthRate / 100), yearsFromStart);
            const annualAmount = calculateAnnualAmount(projectedAmount, source.frequency);
            
            totalIncome += annualAmount;
            
            // Calculate inflation-adjusted value
            const inflationAdjusted = annualAmount / Math.pow(1 + (inflationRate / 100), year - currentYear);
            totalInflationAdjusted += inflationAdjusted;
          }
        });
        
        yearData.totalIncome = Math.round(totalIncome);
        yearData.inflationAdjusted = Math.round(totalInflationAdjusted);
        
      } else if (viewMode === 'by-type') {
        const typeGroups = { salary: 0, business: 0, rental: 0, passive: 0 };
        
        incomeSources.forEach(source => {
          if (year >= source.startYear && year <= source.endYear) {
            const yearsFromStart = year - source.startYear;
            const projectedAmount = source.currentAmount * Math.pow(1 + (source.growthRate / 100), yearsFromStart);
            const annualAmount = calculateAnnualAmount(projectedAmount, source.frequency);
            
            typeGroups[source.type] += annualAmount;
          }
        });
        
        Object.keys(typeGroups).forEach(type => {
          yearData[type] = Math.round(typeGroups[type]);
        });
        
      } else if (viewMode === 'individual') {
        incomeSources.slice(0, 5).forEach(source => { // Limit to 5 sources for readability
          if (year >= source.startYear && year <= source.endYear) {
            const yearsFromStart = year - source.startYear;
            const projectedAmount = source.currentAmount * Math.pow(1 + (source.growthRate / 100), yearsFromStart);
            const annualAmount = calculateAnnualAmount(projectedAmount, source.frequency);
            
            yearData[source.description.substring(0, 15)] = Math.round(annualAmount);
          } else {
            yearData[source.description.substring(0, 15)] = 0;
          }
        });
      }
      
      return yearData;
    });
  };

  // Backend fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ view: viewMode, inflationRate: String(inflationRate), limit: '5' });
        const demoParams = getDemoQueryParams();
        const separator = params.toString() ? '&' : '';
        const fullParams = `${params.toString()}${demoParams ? separator + demoParams.substring(1) : ''}`;
        const res = await fetch(buildApiUrl(`/api/income/projections?${fullParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load income projections');
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) {
          setData(json.data);
        } else {
          setData(generateProjectionData()); // fallback
        }
      } catch (e) {
        console.error(e);
        setData(generateProjectionData()); // fallback to FE calculation
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, inflationRate, incomeSources, projectionHorizon, getDemoQueryParams]);

  // Choose axis unit dynamically (K, Lakh, Crore) based on max value in dataset
  const maxValue = React.useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return 0;
    return data.reduce((maxSoFar, row) => {
      const localMax = Object.entries(row)
        .filter(([k]) => k !== 'year')
        .reduce((m, [, v]) => Math.max(m, Number(v) || 0), 0);
      return Math.max(maxSoFar, localMax);
    }, 0);
  }, [data]);

  const axisTickFormatter = React.useMemo(() => {
    if (maxValue >= 10000000) {
      // Crore
      return (value) => `₹${(value / 10000000).toFixed(1)} Cr`;
    }
    if (maxValue >= 100000) {
      // Lakh
      return (value) => `₹${(value / 100000).toFixed(1)} L`;
    }
    if (maxValue >= 1000) {
      return (value) => `₹${(value / 1000).toFixed(0)} K`;
    }
    return (value) => `₹${Math.round(value).toLocaleString('en-IN')}`;
  }, [maxValue]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-floating">
          <p className="font-medium text-foreground mb-2">{`Year: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatCurrency(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getLineColor = (key, index) => {
    const colors = {
      totalIncome: '#1E40AF',
      inflationAdjusted: '#059669',
      salary: '#1E40AF',
      business: '#059669',
      rental: '#F59E0B',
      passive: '#64748B'
    };
    
    return colors[key] || `hsl(${index * 60}, 70%, 50%)`;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Income Projections</h3>
          <p className="text-sm text-muted-foreground">{`${Math.max(1, Math.min(60, Number(projectionHorizon) || 20))}-year income forecast with growth modeling`}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select
            options={viewModeOptions}
            value={viewMode}
            onChange={setViewMode}
            className="w-40"
          />
          
          {viewMode === 'combined' && (
            <Button
              variant={showInflationAdjusted ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowInflationAdjusted(!showInflationAdjusted)}
              iconName="TrendingDown"
              iconPosition="left"
            >
              Inflation
            </Button>
          )}
        </div>
      </div>

      <div className="relative" style={{ height: '320px' }}>
        {(() => {
          const currentYear = new Date().getFullYear();
          const actualProjectionYears = Math.max(1, Math.min(60, Number(projectionHorizon) || 20));
          const fullTotalYears = data.length;
          const truncationConfig = getChartTruncationConfig('IncomeProjection', canProjectBeyond5Years, isDemoModeActive);
          const shouldTruncate = truncationConfig.shouldTruncate;
          const freeYearsLimit = 5;
          const displayYearsLimit = freeYearsLimit * 2; // 10 years for free users only
          const rawData = shouldTruncate ? data.slice(0, displayYearsLimit + 1) : data;
          const displayData = rawData.map((row, idx) => ({
            ...row,
            isBlurred: shouldTruncate && idx > freeYearsLimit
          }));
          
          // Show year labels every 2 years
          const xAxisTicks = [];
          displayData.forEach((d, i) => {
            if (i % 2 === 0) {
              xAxisTicks.push(d.year);
            }
          });

          return (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={displayData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    {/* Gradient for primary income line */}
                    <linearGradient id="blurGradientIncome" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#1E40AF" stopOpacity="1" />
                      <stop offset="48%" stopColor="#1E40AF" stopOpacity="1" />
                      <stop offset="55%" stopColor="#1E40AF" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#1E40AF" stopOpacity="0.15" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="year" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    ticks={xAxisTicks}
                    height={50}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    tickFormatter={axisTickFormatter}
                  />
                  <Tooltip 
                    content={(props) => {
                      if (props.active && props.payload && props.payload.length) {
                        const isBlurred = props.payload[0]?.payload?.isBlurred;
                        if (isBlurred) {
                          return (
                            <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
                              <p className="font-medium text-muted-foreground flex items-center gap-2">
                                <Icon name="Lock" size={14} />
                                Locked Content
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Upgrade to view full projections
                              </p>
                            </div>
                          );
                        }
                        return <CustomTooltip {...props} />;
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  
                  {/* Render lines with blur effect */}
                  {viewMode === 'combined' ? (
                    <>
                      <Line
                        type="monotone"
                        dataKey="totalIncome"
                        stroke={shouldTruncate ? 'url(#blurGradientIncome)' : '#1E40AF'}
                        strokeWidth={3}
                        name="Total Income"
                        dot={false}
                        strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                      />
                      {showInflationAdjusted && (
                        <Line
                          type="monotone"
                          dataKey="inflationAdjusted"
                          stroke={getLineColor('inflationAdjusted')}
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Inflation Adjusted"
                          dot={false}
                          strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                        />
                      )}
                    </>
                  ) : viewMode === 'by-type' ? (
                    ['salary', 'business', 'rental', 'passive'].map((type, index) => (
                      <Line
                        key={type}
                        type="monotone"
                        dataKey={type}
                        stroke={getLineColor(type)}
                        strokeWidth={2}
                        name={type.charAt(0).toUpperCase() + type.slice(1)}
                        dot={false}
                        strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                        strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                      />
                    ))
                  ) : (
                    incomeSources.slice(0, 5).map((source, index) => (
                      <Line
                        key={source.id}
                        type="monotone"
                        dataKey={source.description.substring(0, 15)}
                        stroke={getLineColor(source.description, index)}
                        strokeWidth={2}
                        name={source.description.substring(0, 15)}
                        dot={false}
                        strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                        strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                      />
                    ))
                  )}
                </LineChart>
              </ResponsiveContainer>
              
              {/* Blurred overlay with gradient and CTA */}
              {shouldTruncate && fullTotalYears > freeYearsLimit && (
                <div 
                  className="absolute top-0 right-0 pointer-events-none"
                  style={{ 
                    left: '48%',
                    bottom: '50px',
                    background: 'linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.5) 8%, rgba(255, 255, 255, 0.85) 25%, rgba(255, 255, 255, 0.95) 60%)',
                    backdropFilter: 'blur(5px)',
                  }}
                >
                  {/* Dark mode gradient overlay */}
                  <div 
                    className="absolute inset-0 dark:block hidden"
                    style={{ 
                      background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.4) 8%, rgba(0, 0, 0, 0.75) 25%, rgba(0, 0, 0, 0.9) 60%)',
                    }}
                  />
                  
                  {/* Upgrade CTA Card */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-2xl p-4 w-[180px] text-white">
                      <div className="flex items-center justify-center mb-3">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                          <Icon name="Lock" size={22} className="text-white" />
                        </div>
                      </div>
                      <h4 className="font-bold text-center mb-1 text-sm">
                        {actualProjectionYears - freeYearsLimit}+ Years Locked
                      </h4>
                      <p className="text-xs text-center text-white/90 mb-3 leading-tight">
                        Your income trajectory extends further.
                      </p>
                      <a
                        href="/upgrade"
                        className="block w-full bg-white text-amber-600 font-semibold text-sm py-2 px-3 rounded-lg text-center hover:bg-amber-50 transition-colors shadow-md"
                      >
                        See My Full Picture
                      </a>
                    </div>
                  </div>
                  
                  {/* Vertical separator line at truncation point */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-amber-400 to-transparent opacity-70"
                    style={{ 
                      boxShadow: '0 0 12px rgba(251, 191, 36, 0.7)'
                    }}
                  />
                </div>
              )}
            </>
          );
        })()}
      </div>

      {viewMode === 'combined' && showInflationAdjusted && (
        <div className="mt-4 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Info" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Inflation Adjustment</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Inflation-adjusted values show purchasing power in today's dollars using {inflationRate}% annual inflation rate.
          </p>
        </div>
      )}
    </div>
  );
};

export default IncomeProjectionChart;